import React, { useState, useEffect } from 'react';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { Megaphone, Image as ImageIcon, DollarSign, Target, CheckCircle, AlertCircle, CreditCard, X } from 'lucide-react';
import { loadIamportScript, initializeIamport, getServicePrice, requestUnifiedPayment, createServicePayment } from '../services/UnifiedPaymentService';

function AdRegistration({ userInfo }) {
  const [formData, setFormData] = useState({
    title: '', description: '', url: '', position: 'top-banner', tier: 'basic', targetBusinessType: 'all',
    advertiserName: userInfo?.businessName || userInfo?.name || '',
    advertiserEmail: userInfo?.email || '',
    advertiserPhone: userInfo?.phone || ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingAdData, setPendingAdData] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [tierPrices, setTierPrices] = useState(null);

  useEffect(() => {
    initializePayment();
    loadPrices();
  }, []);

  const initializePayment = async () => {
    try { await loadIamportScript(); initializeIamport(); }
    catch (error) { console.error('아임포트 초기화 실패:', error); }
  };

  const loadPrices = async () => {
    try { const adPrices = await getServicePrice('advertisement'); setTierPrices(adPrices); }
    catch (error) { console.error('가격 정보 로드 실패:', error); }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { alert('이미지 크기는 5MB 이하여야 합니다.'); return; }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = '광고 제목을 입력해주세요';
    if (!formData.advertiserName.trim()) newErrors.advertiserName = '광고주명을 입력해주세요';
    if (!formData.advertiserEmail.trim()) newErrors.advertiserEmail = '이메일을 입력해주세요';
    if (!imageFile && !imagePreview) newErrors.image = '광고 이미지를 업로드해주세요';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { alert('필수 항목을 모두 입력해주세요.'); return; }
    if (userInfo.companyId) { alert('직원 계정은 광고를 등록할 수 없습니다.'); return; }
    setUploading(true);
    try {
      let imageUrl = '';
      if (imageFile) {
        const storageRef = ref(storage, `advertisements/${Date.now()}_${imageFile.name}`);
        await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(storageRef);
      }
      const adData = { ...formData, imageUrl, status: 'pending', userId: userInfo.uid, createdAt: Timestamp.now(), impressions: 0, clicks: 0, targeting: { businessType: formData.targetBusinessType } };
      const docRef = await addDoc(collection(db, 'advertisements'), adData);
      setPendingAdData({ ...adData, contentId: docRef.id });
      setShowPaymentModal(true);
    } catch (error) { console.error('광고 등록 실패:', error); alert('광고 등록에 실패했습니다.'); }
    finally { setUploading(false); }
  };

  const handlePayment = async () => {
    if (!pendingAdData || !tierPrices) { alert('결제 정보를 불러오는 중입니다.'); return; }
    const tierInfo = tierPrices[pendingAdData.tier];
    if (!tierInfo) { alert('티어 정보를 찾을 수 없습니다.'); return; }
    try {
      setPaymentProcessing(true);
      const paymentResult = await requestUnifiedPayment({
        serviceType: 'advertisement', tier: pendingAdData.tier, amount: tierInfo.price,
        serviceName: `광고: ${pendingAdData.title} (${getTierLabel(pendingAdData.tier)})`,
        buyerName: userInfo.name || userInfo.email, buyerEmail: userInfo.email, buyerPhone: userInfo.phone || '',
        additionalData: { adTitle: pendingAdData.title, position: pendingAdData.position, tier: pendingAdData.tier }
      });
      await createServicePayment({
        userId: userInfo.uid, userInfo, serviceType: 'advertisement', tier: pendingAdData.tier, payment: paymentResult,
        contentId: pendingAdData.contentId, contentData: { title: pendingAdData.title, position: pendingAdData.position, tier: pendingAdData.tier, imageUrl: pendingAdData.imageUrl }
      });
      alert('결제가 완료되었습니다!\n관리자 승인 후 광고가 게시됩니다.');
      setShowPaymentModal(false);
      setPendingAdData(null);
      resetForm();
    } catch (error) { console.error('결제 실패:', error); alert('결제에 실패했습니다.'); }
    finally { setPaymentProcessing(false); }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', url: '', position: 'top-banner', tier: 'basic', targetBusinessType: 'all', advertiserName: userInfo?.businessName || userInfo?.name || '', advertiserEmail: userInfo?.email || '', advertiserPhone: userInfo?.phone || '' });
    setImageFile(null);
    setImagePreview(null);
  };

  const getTierPrice = (tier) => tierPrices?.[tier] ? `${tierPrices[tier].price.toLocaleString()}원` : '로딩 중...';
  const getTierLabel = (tier) => ({ basic: '베이직', standard: '스탠다드', premium: '프리미엄' }[tier] || tier);

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerIconBox}><Megaphone size={20} color="#ffffff" /></div>
          <span style={styles.headerTitle}>광고 등록</span>
        </div>
      </div>

      {/* 안내 */}
      <div style={styles.notice}>
        <AlertCircle size={18} color="#6366f1" />
        <div>
          <strong style={styles.noticeTitle}>광고 승인 안내</strong>
          <p style={styles.noticeText}>제출하신 광고는 관리자 검토 후 승인됩니다.</p>
        </div>
      </div>

      {/* 폼 */}
      <form onSubmit={handleSubmit} style={styles.form}>
        {/* 기본 정보 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>기본 정보</h2>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>광고 제목 *</label>
            <input type="text" name="title" value={formData.title} onChange={handleInputChange} placeholder="예: 최신 임플란트 특가 행사" style={errors.title ? { ...styles.formInput, ...styles.inputError } : styles.formInput} maxLength={50} />
            {errors.title && <span style={styles.errorText}>{errors.title}</span>}
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>광고 설명</label>
            <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="광고에 대한 간단한 설명" style={styles.formTextarea} rows={3} maxLength={200} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>랜딩 URL</label>
            <input type="url" name="url" value={formData.url} onChange={handleInputChange} placeholder="https://example.com" style={styles.formInput} />
          </div>
        </div>

        {/* 이미지 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}><ImageIcon size={18} /> 광고 이미지 *</h2>
          <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} id="adImage" />
          <label htmlFor="adImage" style={styles.uploadBtn}><ImageIcon size={18} /><span>이미지 선택 (최대 5MB)</span></label>
          {errors.image && <span style={styles.errorText}>{errors.image}</span>}
          {imagePreview && <img src={imagePreview} alt="미리보기" style={styles.imagePreview} />}
        </div>

        {/* 설정 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}><Target size={18} /> 광고 설정</h2>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>광고 위치</label>
              <select name="position" value={formData.position} onChange={handleInputChange} style={styles.formSelect}>
                <option value="top-banner">상단 배너</option>
                <option value="sidebar">사이드바</option>
                <option value="footer">하단</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>타겟 업종</label>
              <select name="targetBusinessType" value={formData.targetBusinessType} onChange={handleInputChange} style={styles.formSelect}>
                <option value="all">전체</option>
                <option value="dental">치과</option>
                <option value="lab">기공소</option>
              </select>
            </div>
          </div>

          {/* 티어 선택 */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}><DollarSign size={16} /> 광고 티어 *</label>
            <div style={styles.tierOptions}>
              {[
                { key: 'basic', label: '베이직', features: ['기본 노출', '30일 게시'] },
                { key: 'standard', label: '스탠다드', features: ['우선 노출', '30일 게시', '통계 제공'] },
                { key: 'premium', label: '프리미엄', features: ['최우선 노출', '30일 게시', '상세 통계', '전용 배너'] }
              ].map(tier => (
                <label key={tier.key} style={formData.tier === tier.key ? { ...styles.tierOption, ...styles.tierOptionActive } : styles.tierOption}>
                  <input type="radio" name="tier" value={tier.key} checked={formData.tier === tier.key} onChange={handleInputChange} style={{ display: 'none' }} />
                  <div style={styles.tierName}>{tier.label}</div>
                  <div style={styles.tierPrice}>{getTierPrice(tier.key)}</div>
                  <div style={styles.tierFeatures}>{tier.features.map((f, i) => <div key={i}>• {f}</div>)}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* 광고주 정보 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>광고주 정보</h2>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>광고주명 *</label>
              <input type="text" name="advertiserName" value={formData.advertiserName} onChange={handleInputChange} style={errors.advertiserName ? { ...styles.formInput, ...styles.inputError } : styles.formInput} />
              {errors.advertiserName && <span style={styles.errorText}>{errors.advertiserName}</span>}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>이메일 *</label>
              <input type="email" name="advertiserEmail" value={formData.advertiserEmail} onChange={handleInputChange} style={errors.advertiserEmail ? { ...styles.formInput, ...styles.inputError } : styles.formInput} />
              {errors.advertiserEmail && <span style={styles.errorText}>{errors.advertiserEmail}</span>}
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>연락처</label>
            <input type="tel" name="advertiserPhone" value={formData.advertiserPhone} onChange={handleInputChange} placeholder="010-1234-5678" style={styles.formInput} />
          </div>
        </div>

        {/* 제출 */}
        <button type="submit" disabled={uploading} style={uploading ? { ...styles.submitBtn, ...styles.submitBtnDisabled } : styles.submitBtn}>
          {uploading ? <><div style={styles.spinner}></div><span>업로드 중...</span></> : <><CheckCircle size={18} /><span>다음 단계 (결제)</span></>}
        </button>
      </form>

      {/* 결제 모달 */}
      {showPaymentModal && pendingAdData && tierPrices && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <span style={styles.modalTitle}><CreditCard size={20} /> 광고 결제</span>
              <button onClick={() => setShowPaymentModal(false)} style={styles.modalCloseBtn}><X size={20} /></button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.paymentInfo}>
                <h3 style={styles.paymentInfoTitle}>광고 정보</h3>
                <div style={styles.infoRow}><span style={styles.infoLabel}>광고 제목</span><span style={styles.infoValue}>{pendingAdData.title}</span></div>
                <div style={styles.infoRow}><span style={styles.infoLabel}>광고 티어</span><span style={styles.infoValue}>{getTierLabel(pendingAdData.tier)}</span></div>
                <div style={styles.infoRow}><span style={styles.infoLabel}>광고 위치</span><span style={styles.infoValue}>{pendingAdData.position === 'top-banner' ? '상단 배너' : pendingAdData.position === 'sidebar' ? '사이드바' : '하단'}</span></div>
              </div>

              <div style={styles.paymentSummary}>
                <div style={styles.summaryRow}><span>광고 금액</span><span style={styles.summaryAmount}>{tierPrices[pendingAdData.tier].price.toLocaleString()}원</span></div>
                <div style={styles.summaryRow}><span>게시 기간</span><span>{tierPrices[pendingAdData.tier].duration}일</span></div>
                <div style={styles.summaryDivider}></div>
                <div style={{ ...styles.summaryRow, ...styles.summaryTotal }}><span>총 결제금액</span><span style={styles.totalAmount}>{tierPrices[pendingAdData.tier].price.toLocaleString()}원</span></div>
              </div>

              <div style={styles.paymentNotice}>
                <AlertCircle size={16} />
                <span>결제 완료 후 관리자 승인이 필요합니다</span>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button onClick={() => setShowPaymentModal(false)} style={styles.cancelBtn} disabled={paymentProcessing}>취소</button>
              <button onClick={handlePayment} style={styles.payBtn} disabled={paymentProcessing}>
                {paymentProcessing ? <><div style={styles.smallSpinner}></div><span>처리 중...</span></> : <><CreditCard size={18} /><span>{tierPrices[pendingAdData.tier].price.toLocaleString()}원 결제</span></>}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f1f5f9' },

  // 헤더
  header: { display: 'flex', alignItems: 'center', padding: '14px 16px', backgroundColor: '#6366f1' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  headerIconBox: { width: '36px', height: '36px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  headerTitle: { fontSize: '20px', fontWeight: '700', color: '#ffffff' },

  // 안내
  notice: { display: 'flex', gap: '10px', margin: '14px', padding: '14px', backgroundColor: '#eef2ff', borderRadius: '10px' },
  noticeTitle: { fontSize: '14px', color: '#4338ca' },
  noticeText: { margin: '4px 0 0', fontSize: '12px', color: '#6366f1' },

  // 폼
  form: { padding: '0 14px 14px' },
  section: { backgroundColor: '#ffffff', borderRadius: '14px', padding: '14px', marginBottom: '10px' },
  sectionTitle: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: '0 0 14px' },
  formGroup: { marginBottom: '14px' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  formLabel: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' },
  formInput: { width: '100%', padding: '12px', fontSize: '14px', border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none', boxSizing: 'border-box' },
  inputError: { borderColor: '#ef4444' },
  formTextarea: { width: '100%', padding: '12px', fontSize: '14px', border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' },
  formSelect: { width: '100%', padding: '12px', fontSize: '14px', border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none', backgroundColor: '#fff', boxSizing: 'border-box' },
  errorText: { fontSize: '12px', color: '#ef4444', marginTop: '4px' },

  // 업로드
  uploadBtn: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 20px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#64748b', cursor: 'pointer' },
  imagePreview: { width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '10px', marginTop: '12px' },

  // 티어
  tierOptions: { display: 'flex', flexDirection: 'column', gap: '10px' },
  tierOption: { padding: '14px', backgroundColor: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: '12px', cursor: 'pointer' },
  tierOptionActive: { backgroundColor: '#eef2ff', borderColor: '#6366f1' },
  tierName: { fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' },
  tierPrice: { fontSize: '18px', fontWeight: '700', color: '#6366f1', marginBottom: '8px' },
  tierFeatures: { fontSize: '12px', color: '#64748b', lineHeight: 1.6 },

  // 제출
  submitBtn: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '16px', backgroundColor: '#6366f1', border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '700', color: '#ffffff', cursor: 'pointer' },
  submitBtnDisabled: { backgroundColor: '#94a3b8', cursor: 'not-allowed' },
  spinner: { width: '20px', height: '20px', border: '3px solid rgba(255,255,255,0.3)', borderTop: '3px solid #ffffff', borderRadius: '50%', animation: 'spin 1s linear infinite' },

  // 모달
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 },
  modalContent: { width: '100%', maxWidth: '500px', backgroundColor: '#ffffff', borderTopLeftRadius: '20px', borderTopRightRadius: '20px' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px', borderBottom: '1px solid #f1f5f9' },
  modalTitle: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '17px', fontWeight: '700', color: '#0f172a' },
  modalCloseBtn: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer' },
  modalBody: { padding: '18px' },
  paymentInfo: { padding: '14px', backgroundColor: '#f8fafc', borderRadius: '10px', marginBottom: '14px' },
  paymentInfoTitle: { margin: '0 0 12px', fontSize: '14px', fontWeight: '700', color: '#0f172a' },
  infoRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' },
  infoLabel: { color: '#64748b' },
  infoValue: { fontWeight: '600', color: '#0f172a' },
  paymentSummary: { padding: '14px', backgroundColor: '#f8fafc', borderRadius: '10px', marginBottom: '14px' },
  summaryRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', color: '#64748b' },
  summaryAmount: { fontWeight: '600', color: '#0f172a' },
  summaryDivider: { height: '1px', backgroundColor: '#e2e8f0', margin: '12px 0' },
  summaryTotal: { fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: 0 },
  totalAmount: { fontSize: '18px', color: '#6366f1' },
  paymentNotice: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '8px', fontSize: '12px', color: '#92400e' },
  modalFooter: { display: 'flex', gap: '10px', padding: '18px', borderTop: '1px solid #f1f5f9' },
  cancelBtn: { flex: 1, padding: '14px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#64748b', cursor: 'pointer' },
  payBtn: { flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px', backgroundColor: '#6366f1', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#ffffff', cursor: 'pointer' },
  smallSpinner: { width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #ffffff', borderRadius: '50%', animation: 'spin 1s linear infinite' },
};

export default AdRegistration;